import { Injectable } from '@angular/core';
import { ToastrService } from 'ngx-toastr';
import { WeissmanModalService } from '../Compliance/WeissmanModalService';
import { TaskService } from './task.service.upgrade';
import { TaskModalComponent, TaskModalParams } from './taskModal.component';

import * as _ from 'lodash';

export interface LaunchTaskModalOptions {
    // Passed only by screens that can show the Add Year dialog. When completing a task returns an Add Year prompt, the
    // task modal closes, this handler shows the dialog, and the task modal is opened again once it resolves.
    onAddYearPrompt?: (prompt: Core.AddYearPromptInfo) => Promise<void>;
}

@Injectable({ providedIn: 'root' })
export class LaunchTaskModalService {
    constructor(
        private readonly _taskService: TaskService,
        private readonly _modalService: WeissmanModalService,
        private readonly _toastr: ToastrService,
    ) {}

    async launchTaskModal(
        entityID: number,
        entityTypeID: number,
        readonly?: boolean,
        toastMessage?: string,
        editonly?: boolean,
        options?: LaunchTaskModalOptions,
    ): Promise<boolean> {
        if (toastMessage) {
            this._toastr.info(toastMessage);
        }

        const result = await this._taskService.getByEntity(entityID, entityTypeID);

        // HACK: The task modal doesn't have the concept of "readonly" built-in,
        // but it does read permissions to check if it should allow changes; we'll
        // overwrite the permission data here to put the modal in read-only mode.
        if (readonly) {
            let targetTasks = [];
            if (result && result.tasks) {
                targetTasks = result.tasks;
            }

            _.forEach(targetTasks, (task) => {
                task.taskAuthorizationInfo = {
                    completeTask: false,
                    deleteTask: false,
                    insertTaskAfter: false,
                    insertTaskBefore: false,
                    reassignTask: false,
                    rescindTask: false,
                    skipTask: false,
                };
            });
        }

        if (editonly) {
            let targetTasks = [];
            if (result && result.tasks) {
                targetTasks = result.tasks;
            }
            _.forEach(targetTasks, (task) => {
                if (task.taskAuthorizationInfo) {
                    task.taskAuthorizationInfo.completeTask = false;
                }
            });
        }
        const taskGroupsBySequence = this._taskService.splitIntoGroups(result.tasks);
        const tasksWide = this._taskService.getMaxTasksWide(taskGroupsBySequence);
        const modalSize = tasksWide > 1 ? 'modal-lg ' : 'modal-sm ';
        let windowClass = '';

        if (tasksWide > 1) {
            windowClass = tasksWide < 4 ? `task-modal-${tasksWide}` : 'task-modal-xl';
        }

        let addYearPrompt: Core.AddYearPromptInfo = null;
        const params: TaskModalParams = {
            tasks: taskGroupsBySequence,
            onAddYearPrompt: options?.onAddYearPrompt
                ? (prompt: Core.AddYearPromptInfo): void => {
                      addYearPrompt = prompt;
                  }
                : undefined,
        };

        const shouldReloadTasks = await this._modalService.showAsync(TaskModalComponent, params, modalSize + windowClass, {
            animated: false,
            show: true,
        });

        if (!addYearPrompt) {
            return shouldReloadTasks;
        }

        // Only one modal is open at a time: the task modal closed itself, so show the dialog and then reopen the tasks.
        try {
            await options.onAddYearPrompt(addYearPrompt);
        } finally {
            await this.launchTaskModal(entityID, entityTypeID, readonly, undefined, editonly, options);
        }

        return true;
    }
}
